import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useClientStore } from '@core/client/useClientStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import Header from '@/components/dashboard/Header';
import JobsSummary from '@/components/dashboard/JobsSummary';
import JobsGraph from '@/components/dashboard/JobsGraph';
import TimesheetDisputesTable from '@/components/dashboard/TimesheetDisputesTable';
import UnfilledJobsTable from '@/components/dashboard/UnfilledJobsTable';
import NoJobsSection from '@/components/dashboard/NoJobsSection';
import { JobStatus, DaySequenceStatus } from '@core/generated/gql-types';
import { useTenantId } from '@/contexts/TenantContext';
import { useUiFiltersStore } from '@/stores/useUiFiltersStore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
);

const buildDefaultDashboardRange = () => ({
  startIso: dayjs().startOf('day').toISOString(),
  endIso: dayjs().add(6, 'day').endOf('day').toISOString(),
});

const Dashboard = () => {
  const tenantId = useTenantId();
  const { fetchWorkersForTenant } = useWorkerStore();
  const { jobs, fetchAllJobsForTenantByDateRange, getJobsByStatus } =
    useJobStore();
  const { fetchClientsForTenant } = useClientStore();
  const { byId, fetchAllDaySequencesRequiringAction } = useDaySequenceStore();
  const [isLoading, setIsLoading] = useState(false);
  const dashboardDateRange = useUiFiltersStore(
    (state) => state.dashboardDateRange,
  );
  const setDashboardDateRange = useUiFiltersStore(
    (state) => state.setDashboardDateRange,
  );

  // Build defaults once per mount so the seed-effect and fallback share the same ISO strings,
  // avoiding a double fetch on first visit.
  const fallbackRangeRef = useRef(buildDefaultDashboardRange());

  useEffect(() => {
    if (!dashboardDateRange) {
      setDashboardDateRange(fallbackRangeRef.current);
    }
  }, [dashboardDateRange, setDashboardDateRange]);

  const activeStartIso =
    dashboardDateRange?.startIso ?? fallbackRangeRef.current.startIso;
  const activeEndIso =
    dashboardDateRange?.endIso ?? fallbackRangeRef.current.endIso;

  const timeframeStartDate = useMemo<Dayjs>(
    () => dayjs(activeStartIso),
    [activeStartIso],
  );
  const timeframeEndDate = useMemo<Dayjs>(
    () => dayjs(activeEndIso),
    [activeEndIso],
  );

  // Filter jobs to only include those whose start date is within the selected timeframe.
  // This ensures JobsSummary and JobsGraph use the same filtered logic as jobs table views.
  const timeframeFilteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (!job.startDate) return false;
      const jobStartDate = dayjs(Number(job.startDate));
      return (
        jobStartDate.isSameOrAfter(timeframeStartDate, 'day') &&
        jobStartDate.isSameOrBefore(timeframeEndDate, 'day')
      );
    });
  }, [jobs, timeframeStartDate, timeframeEndDate]);

  // Separate filled and unfilled jobs for child components
  const filledJobs = useMemo(() => {
    return timeframeFilteredJobs.filter(
      (job) =>
        job.itemStatus === JobStatus.Filled ||
        job.itemStatus === JobStatus.Completed,
    );
  }, [timeframeFilteredJobs]);

  const timeframeUnfilledJobs = useMemo(() => {
    return timeframeFilteredJobs.filter(
      (job) => job.itemStatus === JobStatus.Unfilled,
    );
  }, [timeframeFilteredJobs]);

  const unfilledJobs = getJobsByStatus([JobStatus.Unfilled]);

  // Get disputed timesheets (EscalatedToTenant and ProposedChange statuses)
  const disputedTimesheets = React.useMemo(() => {
    const allDaySequences = Object.values(byId);
    return allDaySequences.filter(
      (ds) => ds.status === DaySequenceStatus.EscalatedToTenant,
    );
  }, [byId]);

  const handleDateChange = useCallback(
    (start: Dayjs, end: Dayjs) => {
      const nextStartIso = start.startOf('day').toISOString();
      const nextEndIso = end.endOf('day').toISOString();
      if (nextStartIso === activeStartIso && nextEndIso === activeEndIso) {
        return;
      }

      setDashboardDateRange({
        startIso: nextStartIso,
        endIso: nextEndIso,
      });
    },
    [activeStartIso, activeEndIso, setDashboardDateRange],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchClientsForTenant(tenantId || '', undefined, undefined, true),
        fetchWorkersForTenant(tenantId || ''),
        fetchAllDaySequencesRequiringAction(tenantId || ''),
        fetchAllJobsForTenantByDateRange(
          tenantId || '',
          timeframeStartDate.toISOString(),
          timeframeEndDate.toISOString(),
        ),
      ]);
    } catch (error) {
      const errorString = `Error initializing data for the dashboard. `;

      console.error(errorString, error);

      const toastError =
        error instanceof Error ? error.message : 'An unexpected error occurred';

      toast.error(toastError, {
        position: 'bottom-left',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    tenantId,
    fetchClientsForTenant,
    fetchWorkersForTenant,
    fetchAllDaySequencesRequiringAction,
    fetchAllJobsForTenantByDateRange,
    timeframeStartDate,
    timeframeEndDate,
  ]);

  useEffect(() => {
    if (tenantId) {
      fetchAllJobsForTenantByDateRange(
        tenantId || '',
        timeframeStartDate.toISOString(),
        timeframeEndDate.toISOString(),
      );
    }
  }, [
    tenantId,
    timeframeStartDate,
    timeframeEndDate,
    fetchAllJobsForTenantByDateRange,
  ]);

  useEffect(() => {
    if (tenantId) {
      fetchData();
    }
  }, [tenantId, fetchData]);

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <Header
        startDate={timeframeStartDate}
        endDate={timeframeEndDate}
        handleDateChange={handleDateChange}
        refreshData={fetchData}
      />
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <JobsSummary
          filledJobs={filledJobs}
          unfilledJobs={timeframeUnfilledJobs}
          timeframeStartDate={timeframeStartDate}
        />
        <JobsGraph
          filledJobs={filledJobs}
          unfilledJobs={timeframeUnfilledJobs}
          startDate={timeframeStartDate}
          endDate={timeframeEndDate}
        />
      </div>
      {unfilledJobs.length > 0 || disputedTimesheets.length > 0 ? (
        <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <TimesheetDisputesTable disputedTimesheets={disputedTimesheets} />
          <UnfilledJobsTable
            allFilteredJobs={unfilledJobs}
            timeframeStartDate={timeframeStartDate}
            timeframeEndDate={timeframeEndDate}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <NoJobsSection />
      )}
    </div>
  );
};

export default Dashboard;
